import { fetchPdmProductsRecords } from "@views/Upholstery/pdm/pdmStepsClient"
import { deriveVehicleCatalogFromPdm } from "../../pdm/deriveVehicleCatalogFromPdm"
import { usePdmVehicleCatalogStore } from "../../store/pdm-vehicle-catalog-store"

type LoadPdmVehicleCatalogOptions = {
  /** Log catalog JSON to console (upholstery page). */
  log?: boolean
}

export const PDM_CATALOG_LOAD_ERROR =
  "Unable to load vehicles. Please try again."

/** Total fetch attempts before showing an error (1 initial + 2 retries). */
const MAX_ATTEMPTS = 3

/** Delay before each retry (ms). */
const RETRY_DELAYS_MS = [2_000, 4_000]

let catalogLoadGeneration = 0
let catalogLoadInflight: Promise<void> | null = null

/**
 * Set only after a successful live fetch in this browser tab.
 * Persist/localStorage cars do not count — a hard refresh still refreshes once.
 */
let catalogLoadedThisSession = false

/** Stop in-flight catalog load / retry loop (tests or explicit reset only). */
export function cancelPdmVehicleCatalogLoad(): void {
  catalogLoadGeneration += 1
  catalogLoadInflight = null
  catalogLoadedThisSession = false
}

/** Reset session flag (tests). Normal navigation must not call this. */
export function resetPdmVehicleCatalogSessionLoad(): void {
  catalogLoadedThisSession = false
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchAndPopulateCatalog(
  generation: number,
  hadCachedCatalog: boolean,
  options?: LoadPdmVehicleCatalogOptions
): Promise<void> {
  const { actions } = usePdmVehicleCatalogStore.getState()

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (generation !== catalogLoadGeneration) {
      return
    }

    try {
      const products = await fetchPdmProductsRecords()
      if (generation !== catalogLoadGeneration) {
        return
      }

      if (products?.length) {
        const { catalog, stats } = deriveVehicleCatalogFromPdm(products)

        if (options?.log) {
          console.group("[UpholsteryV2] PDM vehicle catalog")
          console.log(
            JSON.stringify(
              {
                summary: stats,
                catalog,
                dato_list_skipped: true,
              },
              null,
              2
            )
          )
          console.groupEnd()
        }

        if (generation !== catalogLoadGeneration) {
          return
        }

        actions.setPickerCars(catalog, stats)
        catalogLoadedThisSession = true
        return
      }

      console.warn(
        `[UpholsteryV2] PDM catalog empty (attempt ${
          attempt + 1
        }/${MAX_ATTEMPTS})`
      )
    } catch (err) {
      console.error(
        `[UpholsteryV2] PDM catalog fetch failed (attempt ${
          attempt + 1
        }/${MAX_ATTEMPTS})`,
        err
      )
    }

    if (attempt < MAX_ATTEMPTS - 1) {
      await sleep(RETRY_DELAYS_MS[attempt] ?? 4_000)
    }
  }

  if (generation !== catalogLoadGeneration) {
    return
  }

  const stillEmpty =
    usePdmVehicleCatalogStore.getState().pickerCars.length === 0

  if (stillEmpty && !hadCachedCatalog) {
    actions.setError(PDM_CATALOG_LOAD_ERROR)
  }
}

/**
 * Fetch PDM products and populate `PdmVehicleCatalogStore` (client-only).
 * Shows persisted catalog immediately when present.
 * After one successful live load this tab, later calls are no-ops (navigate freely).
 */
export async function loadPdmVehicleCatalogIntoStore(
  options?: LoadPdmVehicleCatalogOptions
): Promise<void> {
  if (catalogLoadedThisSession) {
    return
  }

  if (catalogLoadInflight) {
    return catalogLoadInflight
  }

  const state = usePdmVehicleCatalogStore.getState()
  const hadCachedCatalog = state.pickerCars.length > 0

  const generation = catalogLoadGeneration
  state.actions.setError(null)

  if (!hadCachedCatalog) {
    state.actions.setLoading(true)
  }

  catalogLoadInflight = fetchAndPopulateCatalog(
    generation,
    hadCachedCatalog,
    options
  ).finally(() => {
    if (generation === catalogLoadGeneration) {
      catalogLoadInflight = null
    }

    const current = usePdmVehicleCatalogStore.getState()
    if (generation === catalogLoadGeneration) {
      current.actions.setLoading(false)
    } else if (current.pickerCars.length === 0) {
      current.actions.setLoading(false)
    }
  })

  return catalogLoadInflight
}
